import { COMMERCE_CARDS, commerceTemplate } from '../../../server/src/lib/templates/catalogue'
import { withEngineActions } from '../../../server/src/lib/templates/engineActions'
import type { WidgetTemplate } from '@/features/conversation/types/message'

/** A card as it arrives in a message: Pair's own widget template, an agent component, or a name with data. */
export type Card = Record<string, unknown>

export interface ResolveOptions {
  /** ar | en. Default: the card's own language (Arabic text in it means ar). */
  lang?: 'ar' | 'en'
  /**
   * A studio that knows every template (e.g. https://widget-profiessanl.prod.k8s.trypair.ai).
   * Used only for a template this SDK cannot resolve on its own; without it such a card is an error.
   */
  resolveUrl?: string
}

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v)

function langOf(card: Card, given?: string): 'ar' | 'en' {
  if (given === 'ar' || given === 'en') return given
  if (card.lang === 'ar' || card.lang === 'en') return card.lang
  const content = card.content as { lang?: string } | undefined
  if (content?.lang === 'ar' || content?.lang === 'en') return content.lang
  return /[؀-ۿ]/.test(JSON.stringify(card)) ? 'ar' : 'en'
}

/**
 * The card as the widget's renderer takes it. Native cards, commerce cards, movie_cards and
 * price_list with supplied items resolve here with no network; any other studio template is asked of `resolveUrl` once.
 */
export async function resolveCard(card: Card, opts: ResolveOptions = {}): Promise<WidgetTemplate> {
  if (!isObj(card)) throw new Error('A card must be an object')
  const lang = langOf(card, opts.lang)
  const type = typeof card.type === 'string' ? card.type : ''

  // 1. Pair's widget template: drawn as it is.
  if (type && type !== 'template' && isObj(card.content)) {
    const { lang: _l, theme: _t, ...native } = card
    return { ...native, content: { lang, ...(card.content as object) } } as unknown as WidgetTemplate
  }

  // A template by name: self-contained cards resolve locally.
  const name = typeof card.template === 'string' ? card.template : ''
  if (!name) throw new Error('Not a card: expected {type, content} or {template, data}')
  // an agent's card may put its own actions behind the card's buttons ({add_to_cart: "danah.add_to_cart"} or {id, data})
  if (COMMERCE_CARDS[name]) {
    const data = isObj(card.data) ? card.data : {}
    return withEngineActions(commerceTemplate(name, data, lang) as unknown as WidgetTemplate, card.actions)
  }

  // Movie data is already complete in the reply; no studio source needs to be hydrated.
  if (name === 'movie_cards') {
    const data = isObj(card.data) ? card.data : {}
    return {
      type: 'movie_cards',
      content: {
        ...data,
        lang,
        items: Array.isArray(data.items) ? data.items.filter(isObj) : [],
        // Match the studio's movie_cards defaults, while preserving supplied overrides.
        design: {
          radius: 16, padding: 10, titleSize: 13, columns: 2, imageRatio: '2:3',
          ...(isObj(data.design) ? data.design : {}),
        },
      },
    } as unknown as WidgetTemplate
  }

  // Supplied prices are self-contained. Empty price lists still need the studio's bound source.
  if (name === 'price_list' && isObj(card.data) && Array.isArray(card.data.items) && card.data.items.length > 0) {
    const data = card.data
    return {
      type: 'price_list',
      content: {
        title: 'أسعار التذاكر 🎬',
        title_en: 'Ticket prices 🎬',
        currency: 'د.ك',
        currency_en: 'KD',
        ...data,
        lang,
        items: (data.items as unknown[]).filter(isObj),
        design: {
          radius: 16, padding: 16, titleSize: 14, divider: 'dashed',
          ...(isObj(data.design) ? data.design : {}),
        },
      },
    } as unknown as WidgetTemplate
  }

  // Any other studio template: one call to a studio that knows it.
  if (!opts.resolveUrl) {
    throw new Error(`Template "${name}" needs the studio to resolve it: pass resolveUrl, or send Pair's widget template`)
  }
  const res = await fetch(`${opts.resolveUrl.replace(/\/$/, '')}/api/public/render?format=json`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...card, lang }),
  })
  const body = (await res.json().catch(() => ({}))) as { template?: WidgetTemplate; error?: { message?: string } }
  if (!res.ok || !body.template) throw new Error(body.error?.message || `Studio answered ${res.status}`)
  return withEngineActions(body.template, card.actions)
}

/** Locally resolved template names; price_list requires supplied items (empty lists need the studio). */
export const LOCAL_TEMPLATES = [...Object.keys(COMMERCE_CARDS), 'movie_cards', 'price_list']
